import { Component, createRef } from 'react';
import { container, lazyInject } from '../../../context/module';
import { ScriptLoadService } from '../../../services/script-load.service';
import { ConnectivityService } from '../../../services/connectivity.service';
import { GAService } from '../../../services/analytics.service';
import { GAContants } from '../../../types/ga.types';
import { FileSelectEvent, FileUploadProps } from '../../../types/file-upload.types';
import FileUploadComponent from '../file-upload/file-upload.component';
import styles from './drop-zone.component.module.scss';

interface DropZoneProps {
    acceptedExtensions?: string[];
    fileUploadProps: FileUploadProps;
}

interface DropZoneState {
    ready: boolean;
    dragging: boolean;
}

export default class DropZoneComponent extends Component<DropZoneProps, DropZoneState> {
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(GAService) private gaService: GAService;

    readonly state: Readonly<DropZoneState> = { ready: false, dragging: false };

    async componentDidMount() {
        try { container.get(ConnectivityService).preWarm(); } catch { /* optional */ }
        try {
            await this.scriptLoadService.loadAllScriptsIfNotLoaded(true);
        } catch {
            // proceed — core upload uses npm resumablejs
        }
        this.setState({ ready: true });
    }

    private handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.dragging) this.setState({ dragging: true });
    };

    private handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ dragging: false });
    };

    private handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ dragging: false });
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-CLICKED'],
                GAContants.EventLabel.UPLOAD,
                '',
            );
            this.props.fileUploadProps.fileSelected?.({ files });
        }
    };

    private getFormatsLabel(): string {
        // "file" is an internal wildcard extension (it drives the generic convert-file-to-*
        // routes so any upload is accepted) — it's not a real format, so never show it here.
        const exts = (this.props.acceptedExtensions || []).filter(e => e.toLowerCase() !== 'file');
        if (exts.length === 0) return '';
        const shown = exts.slice(0, 6).map(e => e.toUpperCase()).join(', ');
        return exts.length > 6 ? `${shown} +${exts.length - 6} more` : shown;
    }

    render() {
        const { ready, dragging } = this.state;
        const formatsLabel = this.getFormatsLabel();

        return (
            <div
                className={`${styles.dropZone} ${dragging ? styles.dragging : ''} ${!ready ? styles.loading : ''}`}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={ready ? this.handleDrop : undefined}
                role="region"
                aria-label="File upload area"
            >
                {!ready ? (
                    <div className={styles.readyContent}>
                        <FileUploadComponent
                            {...this.props.fileUploadProps}
                            shouldDisplayBtnsAsPrimary={true}
                        />
                        <p className={styles.hintText}>Initializing... drag & drop files here</p>
                        {formatsLabel && (
                            <p className={styles.formatsText}>Supports: {formatsLabel}</p>
                        )}
                    </div>
                ) : (
                    <div className={styles.readyContent}>
                        <FileUploadComponent
                            {...this.props.fileUploadProps}
                            shouldDisplayBtnsAsPrimary={true}
                        />
                        <p className={styles.hintText}>
                            {dragging ? 'Drop files here' : 'or drag & drop files here'}
                        </p>
                        {formatsLabel && (
                            <p className={styles.formatsText}>Supports: {formatsLabel}</p>
                        )}
                    </div>
                )}
            </div>
        );
    }
}
